import { action, card, editor, field, group, iconAction, muted, region, title, toolbar, type Field, type View } from "./components"
import { datalist, label, option, palette, select, span } from "./html"

export type FormSchema = {
  type?: string
  title?: string
  description?: string
  format?: string
  const?: unknown
  enum?: unknown[]
  minItems?: number
  properties?: Record<string, FormSchema>
  required?: string[]
  items?: FormSchema
}

export type SchemaFormOptions = {
  choices?: Record<string, unknown[]>
  title?: string
  saveLabel?: string
  readonly?: string[]
  hidden?: string[]
}

type Control = {view: View, read(): unknown}
const omitted = Symbol("omitted")

function caption(name: string, schema: FormSchema, required: boolean) {
  return span((schema.title ?? name.replaceAll("_", " ")) + (required ? " *" : "")).style({
    display: "block", color: palette.mutedText, fontSize: "13px", fontWeight: "600", marginBottom: "6px",
    textTransform: "capitalize",
  })
}

function defaultValue(schema: FormSchema | undefined): unknown {
  if (!schema) return ""
  if (schema.const !== undefined) return structuredClone(schema.const)
  if (schema.enum?.length) return structuredClone(schema.enum[0])
  if (schema.type === "array") return Array.from({length: schema.minItems ?? 0}, () => defaultValue(schema.items))
  if (schema.type === "object") return Object.fromEntries((schema.required ?? []).map(name => [name, defaultValue(schema.properties?.[name])]))
  if (schema.type === "boolean") return false
  if (schema.type === "number" || schema.type === "integer") return 0
  return ""
}

function choiceControl(choices: unknown[], value: unknown): Control {
  const values = choices.some(choice => JSON.stringify(choice) === JSON.stringify(value)) ? choices : [value, ...choices]
  const control = select(...values.map((choice, index) => option(
    Array.isArray(choice) && choice.every(part => typeof part === "string") ? choice.join("/") || "Choose…" : typeof choice === "string" ? choice : JSON.stringify(choice),
  ).setAttribute("value", String(index))))
  control.el.value = String(Math.max(0, values.findIndex(choice => JSON.stringify(choice) === JSON.stringify(value))))
  control.style({padding: "9px 11px", boxSizing: "border-box", width: "100%"})
  return {view: control, read: () => structuredClone(values[Number(control.el.value)])}
}

function scalarControl(name: string, schema: FormSchema, value: unknown): Control {
  if (schema.const !== undefined) return {view: muted(String(schema.const)), read: () => schema.const}
  if (schema.type === "boolean") {
    const control = field("", {type: "checkbox"})
    control.el.checked = value === true
    control.style({width: "auto"})
    return {view: control, read: () => control.el.checked}
  }
  if (schema.format === "textarea") {
    const control = editor(value == null ? "" : String(value))
    return {view: control, read: () => control.get()}
  }
  const control: Field = field(value == null ? "" : String(value), schema.type === "number" || schema.type === "integer" ? {type: "number"} : {})
  let suggestions: View | undefined
  if (schema.enum?.length) {
    const id = `schema-${crypto.randomUUID()}`
    control.setAttribute("list", id)
    suggestions = datalist(...schema.enum.map(item => option().setAttribute("value", String(item)))).setAttribute("id", id)
  }
  return {
    view: group(control, suggestions ?? ""),
    read: () => schema.type === "number" || schema.type === "integer" ? Number(control.get()) : control.get(),
  }
}

function optionalControl(name: string, schema: FormSchema, value: unknown, required: boolean, options: SchemaFormOptions): Control {
  let enabled = required || value !== undefined
  const content = region()
  const control = valueControl(name, schema, value ?? defaultValue(schema), required, options)
  const render = () => content.show(enabled
    ? group(control.view, required ? "" : action("Remove field", () => { enabled = false; render() }))
    : action(`Add ${schema.title ?? name.replaceAll("_", " ")}`, () => { enabled = true; render() }))
  render()
  return {view: content, read: () => enabled ? control.read() : omitted}
}

function valueControl(name: string, schema: FormSchema, value: unknown, required: boolean, options: SchemaFormOptions): Control {
  const choices = schema.enum ?? (schema.format ? options.choices?.[schema.format] : undefined)
  if (choices?.length) {
    const control = choiceControl(choices, value)
    return {view: label(caption(name, schema, required), control.view, schema.description ? muted(schema.description) : "").style({display: "block", margin: "14px 0"}), read: control.read}
  }
  if (schema.type === "object" && schema.properties) {
    const controls = Object.entries(schema.properties).map(([key, child]) => [key, optionalControl(key, child, (value as any)?.[key], schema.required?.includes(key) ?? false, options)] as const)
    return {
      view: card(title(schema.title ?? name.replaceAll("_", " ")).style({fontSize: "15px", margin: "0"}), ...controls.map(([, control]) => control.view)),
      read: () => {
        const known = new Set(Object.keys(schema.properties ?? {}))
        const existing = typeof value === "object" && value && !Array.isArray(value) ? Object.entries(value).filter(([key]) => !known.has(key)) : []
        const entries = controls.flatMap(([key, control]) => {
          const next = control.read()
          return next === omitted ? [] : [[key, next] as [string, unknown]]
        })
        return Object.fromEntries([...existing, ...entries])
      },
    }
  }
  if (schema.type === "array" && schema.items) {
    let values = Array.isArray(value) ? value : []
    while (values.length < (schema.minItems ?? 0)) values.push(defaultValue(schema.items))
    const content = region()
    let controls: Control[] = []
    const render = () => {
      controls = values.map((item, index) => valueControl(`${name} ${index + 1}`, schema.items!, item, true, options))
      content.show(
        ...controls.map((control, index) => card(
          control.view,
          values.length > (schema.minItems ?? 0) ? action("Remove", () => {
            values = controls.map(entry => entry.read())
            values.splice(index, 1)
            render()
          }) : "",
        )),
        action(`Add ${schema.items?.title ?? name.replace(/s$/, "")}`, () => {
          values = controls.map(entry => entry.read())
          values.push(defaultValue(schema.items))
          render()
        }),
      )
    }
    render()
    return {
      view: card(caption(name, schema, required), content),
      read: () => controls.map(control => control.read()),
    }
  }
  if (schema.type === "array" || (schema.type === "object" && !schema.properties)) {
    const control = editor(JSON.stringify(value ?? (schema.type === "array" ? [] : {}), null, 2), ".json")
    return {
      view: label(caption(name, schema, required), control, schema.description ? muted(schema.description) : "").style({display: "block", margin: "14px 0"}),
      read: () => JSON.parse(control.get()),
    }
  }
  const control = scalarControl(name, schema, value)
  return {
    view: label(caption(name, schema, required), control.view, schema.description ? muted(schema.description) : "").style({display: "block", margin: "14px 0"}),
    read: control.read,
  }
}

export function schemaForm(
  schema: FormSchema,
  value: Record<string, unknown>,
  save: (value: Record<string, unknown>) => Promise<void>,
  cancel: (() => void) | undefined,
  report: (error: unknown) => void,
  options: SchemaFormOptions = {},
) {
  const properties = schema.properties ?? {}
  const controls = Object.entries(properties).map(([name, child]) => [name, options.hidden?.includes(name)
    ? {view: group(), read: () => value[name]}
    : options.readonly?.includes(name)
      ? {view: label(caption(name, child, schema.required?.includes(name) ?? false), muted(String(value[name] ?? ""))).style({display: "block", margin: "14px 0"}), read: () => value[name]}
      : optionalControl(name, child, value[name], schema.required?.includes(name) ?? false, options)] as const)
  const raw = controls.length ? undefined : editor(JSON.stringify(value, null, 2), ".json")
  return card(
    title(options.title ?? "Edit item").style({fontSize: "17px", marginTop: "0"}),
    controls.length ? group(...controls.map(([, control]) => control.view)) : label(caption("JSON", {}, true), raw!).style({display: "block", margin: "14px 0"}),
    toolbar(iconAction(options.saveLabel ?? "Save changes", "save", async () => {
      try {
        const known = new Set(Object.keys(properties))
        const unknown = Object.entries(value).filter(([name]) => !known.has(name))
        const entries = controls.flatMap(([name, control]) => {
          const next = control.read()
          return next === omitted ? [] : [[name, next] as [string, unknown]]
        })
        const next = raw ? JSON.parse(raw.get()) : Object.fromEntries([...unknown, ...entries])
        await save(next)
      } catch (error) { report(error) }
    }), ...(cancel ? [action("Cancel", cancel)] : [])),
  )
}
